/**
 * تولیدکننده منوهای غذایی با منطق گروه‌بندی
 * کپی شده از new_front/src/pages/regime/steps/myFavMenu.jsx
 */
import moment from 'jalali-moment';
import { customRound, customRound2, customRoundFull, categorizeFraction } from './roundingUtils';
import { updateMenuHistory } from './menuHistory';

/**
 * تولید منوی یک وعده با منطق گروه‌بندی
 * @param {string} mealType - نوع وعده (صبحانه، ناهار، شام، ...)
 * @param {number} calories - کالری مورد نیاز
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {Object} selectedMenusHistory - تاریخچه منوهای انتخاب شده
 * @param {string} currentDate - تاریخ فعلی
 * @param {Function} onHistoryUpdate - تابع callback برای به‌روزرسانی تاریخچه
 * @param {Object} todaySelectedMenusRef - ref برای منوهای انتخاب شده امروز
 * @param {Array} previousDaysMenus - منوهای روزهای قبلی
 * @returns {Object|null} - منوی تولید شده یا null
 */
export const generateMealMenu = (
  mealType,
  calories,
  formattedData,
  selectedMenusHistory,
  currentDate,
  onHistoryUpdate = null,
  todaySelectedMenusRef = { menus: [] },
  previousDaysMenus = []
) => {
  try {
    // بررسی پیش‌نیازها
    if (!calories || calories <= 0) {
      console.warn(`مقدار ${mealType}Cal نامعتبر است:`, calories);
      return null;
    }

    if (formattedData.length === 0) {
      console.warn("داده‌های منوها خالی هستند.");
      return null;
    }

    // فیلتر منوهای مربوط به این وعده
    // فقط منوهایی که form_type === "menu" یا "food_menu" دارند و selected_food یک رشته غیرخالی است
    // استفاده از String.trim() برای اطمینان از تطابق دقیق mealType (مطابق myFavMenu.jsx)
    let mealMenus = formattedData.filter((menu) => {
      // تطابق دقیق mealType (با trim برای حذف فاصله‌های اضافی)
      const menuMeal = String(menu.meal || '').trim();
      const targetMealType = String(mealType || '').trim();
      
      return menuMeal === targetMealType && 
        menu.items.length > 0 &&
        (menu.form_type === "menu" || menu.form_type === "food_menu") &&
        menu.selected_food !== null && 
        menu.selected_food !== undefined &&
        typeof menu.selected_food === "string" && 
        menu.selected_food.trim().length > 0;
    });
    
    if (mealMenus.length === 0) {
      console.error(`[${mealType}] ❌ هیچ منوی ${mealType} معتبری یافت نشد.`);
      return null;
    }

    // محاسبه تاریخ دیروز و 2 روز قبل
    const yesterdayDate = moment(currentDate, 'YYYY/MM/DD').subtract(1, 'days').format('YYYY/MM/DD');
    const twoDaysAgoDate = moment(currentDate, 'YYYY/MM/DD').subtract(2, 'days').format('YYYY/MM/DD');
    
    // استخراج منوهای امروز و دیروز از تاریخچه
    const todayMenus = selectedMenusHistory.daily.filter((entry) => entry.date === currentDate);
    let yesterdayMenus = selectedMenusHistory.daily.filter((entry) => entry.date === yesterdayDate);
    
    // اضافه کردن منوهای تولید شده در روزهای قبلی
    const previousDayMenusFromLoop = previousDaysMenus.filter((entry) => entry.date === yesterdayDate);
    yesterdayMenus = [...yesterdayMenus, ...previousDayMenusFromLoop];
    
    // استخراج منوهای 2 روز قبل
    const twoDaysAgoMenus = selectedMenusHistory.daily.filter((entry) => entry.date === twoDaysAgoDate);
    const previousTwoDaysAgoMenusFromLoop = previousDaysMenus.filter((entry) => entry.date === twoDaysAgoDate);
    const allTwoDaysAgoMenus = [...twoDaysAgoMenus, ...previousTwoDaysAgoMenusFromLoop];
    
    // اضافه کردن منوهای انتخاب شده در همان روز
    const todayMenusIncludingCurrent = [...todayMenus, ...todaySelectedMenusRef.menus];

    // محاسبه تعداد استفاده از هر گروه در هفته جاری
    const groupUsageCount = {};
    const currentWeekStart = moment(currentDate, 'YYYY/MM/DD').startOf('week');
    const currentWeekEnd = moment(currentDate, 'YYYY/MM/DD').endOf('week');
    
    // محاسبه groupUsageCount برای همه وعده‌ها
    selectedMenusHistory.daily.forEach((entry) => {
      const entryDate = moment(entry.date, 'YYYY/MM/DD');
      const isInCurrentWeek = entryDate.isSameOrAfter(currentWeekStart, 'day') && 
                              entryDate.isSameOrBefore(currentWeekEnd, 'day');
      
      if (isInCurrentWeek) {
        const menu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (menu && menu.group_number && menu.group_title) {
          const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
          if (!groupUsageCount[groupKey]) {
            groupUsageCount[groupKey] = 0;
          }
          groupUsageCount[groupKey]++;
        }
      }
    });
    
    // اضافه کردن منوهای انتخاب شده در همان روز
    todaySelectedMenusRef.menus.forEach((entry) => {
      const menu = formattedData.find(m => String(m.id) === String(entry.menuId));
      if (menu && menu.group_number && menu.group_title) {
        const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
        if (!groupUsageCount[groupKey]) {
          groupUsageCount[groupKey] = 0;
        }
        groupUsageCount[groupKey]++;
      }
    });
    
    // اضافه کردن منوهای تولید شده در روزهای قبلی
    previousDaysMenus.forEach((entry) => {
      const entryDate = moment(entry.date, 'YYYY/MM/DD');
      const isInCurrentWeek = entryDate.isSameOrAfter(currentWeekStart, 'day') && 
                              entryDate.isSameOrBefore(currentWeekEnd, 'day');
      
      if (isInCurrentWeek && entry.menuId) {
        const menu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (menu && menu.group_number && menu.group_title) {
          const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
          if (!groupUsageCount[groupKey]) {
            groupUsageCount[groupKey] = 0;
          }
          groupUsageCount[groupKey]++;
        }
      }
    });

    // جدا کردن منوهای دارای گروه و بدون گروه
    const menusWithGroups = mealMenus.filter(menu => menu.group_number && menu.group_title && menu.group_repetition_count);
    const menusWithoutGroups = mealMenus.filter(menu => !menu.group_number || !menu.group_title || !menu.group_repetition_count);

    // تابع کمکی برای بررسی قوانین مهم (که همیشه باید رعایت شوند)
    const checkCriticalRules = (menu) => {
      const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
      
      // قانون مهم 1: اگر در امروز برای وعده‌های دیگر از این گروه استفاده شده، نباید استفاده شود
      const usedTodayOtherMeals = todayMenusIncludingCurrent.some((entry) => {
        const entryMenu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (entryMenu && entryMenu.group_number && entryMenu.group_title) {
          return String(entryMenu.group_number) === String(menu.group_number) && 
                 String(entryMenu.group_title) === String(menu.group_title) &&
                 entry.meal !== mealType;
        }
        return false;
      });

      if (usedTodayOtherMeals) {
        return { valid: false, reason: `در وعده‌های دیگر امروز استفاده شده` };
      }

      // قانون مهم 2: اگر در دیروز برای همین وعده از این گروه استفاده شده، نباید استفاده شود
      const usedYesterdaySameMeal = yesterdayMenus.some((entry) => {
        const entryMenu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (entryMenu && entryMenu.group_number && entryMenu.group_title) {
          return String(entryMenu.group_number) === String(menu.group_number) && 
                 String(entryMenu.group_title) === String(menu.group_title) &&
                 entry.meal === mealType;
        }
        return false;
      });

      if (usedYesterdaySameMeal) {
        return { valid: false, reason: `دیروز برای همین وعده استفاده شده` };
      }
      
      // قانون مهم 3: اگر در امروز برای همین وعده از این گروه استفاده شده، نباید دوباره استفاده شود
      const usedTodaySameMeal = todayMenusIncludingCurrent.some((entry) => {
        const entryMenu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (entryMenu && entryMenu.group_number && entryMenu.group_title) {
          return String(entryMenu.group_number) === String(menu.group_number) && 
                 String(entryMenu.group_title) === String(menu.group_title) &&
                 entry.meal === mealType;
        }
        return false;
      });

      if (usedTodaySameMeal) {
        return { valid: false, reason: `امروز برای همین وعده استفاده شده` };
      }

      return { valid: true };
    };

    // فیلتر کردن منوهای دارای گروه بر اساس محدودیت‌ها
    let availableGroupMenus = menusWithGroups.filter((menu) => {
      const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
      const groupUsage = groupUsageCount[groupKey] || 0;
      const repetitionCount = menu.group_repetition_count || 0;

      // بررسی قوانین مهم (که همیشه باید رعایت شوند)
      const criticalCheck = checkCriticalRules(menu);
      if (!criticalCheck.valid) {
        return false;
      }

      // محدودیت 4: اگر تعداد استفاده شده از گروه به سقف تکرار رسیده، نباید استفاده شود (اما در حالت اضطراری می‌تواند استفاده شود)
      if (groupUsage >= repetitionCount) {
        return false;
      }

      return true;
    });

    // فیلتر کردن منوهایی که در 2 روز گذشته در همان گروه استفاده شده‌اند
    const menusUsedInLastTwoDaysByGroup = {};
    
    yesterdayMenus.forEach((entry) => {
      if (entry.meal === mealType && entry.menuId) {
        const entryMenu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (entryMenu && entryMenu.group_number && entryMenu.group_title) {
          const groupKey = `${String(entryMenu.group_number)}_${String(entryMenu.group_title)}`;
          if (!menusUsedInLastTwoDaysByGroup[groupKey]) {
            menusUsedInLastTwoDaysByGroup[groupKey] = new Set();
          }
          menusUsedInLastTwoDaysByGroup[groupKey].add(String(entry.menuId));
        }
      }
    });
    
    allTwoDaysAgoMenus.forEach((entry) => {
      if (entry.meal === mealType && entry.menuId) {
        const entryMenu = formattedData.find(m => String(m.id) === String(entry.menuId));
        if (entryMenu && entryMenu.group_number && entryMenu.group_title) {
          const groupKey = `${String(entryMenu.group_number)}_${String(entryMenu.group_title)}`;
          if (!menusUsedInLastTwoDaysByGroup[groupKey]) {
            menusUsedInLastTwoDaysByGroup[groupKey] = new Set();
          }
          menusUsedInLastTwoDaysByGroup[groupKey].add(String(entry.menuId));
        }
      }
    });
    
    // جدا کردن منوهایی که در 2 روز گذشته استفاده شده‌اند و آن‌هایی که نشده‌اند
    const menusNotUsedInLastTwoDays = availableGroupMenus.filter((menu) => {
      const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
      const usedMenus = menusUsedInLastTwoDaysByGroup[groupKey];
      if (!usedMenus) {
        return true;
      }
      return !usedMenus.has(String(menu.id));
    });
    
    const menusUsedInLastTwoDaysList = availableGroupMenus.filter((menu) => {
      const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
      const usedMenus = menusUsedInLastTwoDaysByGroup[groupKey];
      if (!usedMenus) {
        return false;
      }
      return usedMenus.has(String(menu.id));
    });

    // انتخاب منو
    let selectedMenu = null;

    // اولویت 1: اگر منوهای دارای گروه موجود هستند، از آن‌ها انتخاب کن
    if (availableGroupMenus.length > 0) {
      // اولویت 1.1: اگر منوهایی که در 2 روز گذشته استفاده نشده‌اند موجود هستند، از آن‌ها انتخاب کن
      if (menusNotUsedInLastTwoDays.length > 0) {
        const randomIndex = Math.floor(Math.random() * menusNotUsedInLastTwoDays.length);
        selectedMenu = menusNotUsedInLastTwoDays[randomIndex];
      }
      // اولویت 1.2: اگر همه منوها در 2 روز گذشته استفاده شده‌اند، از همان منوها انتخاب کن
      else if (menusUsedInLastTwoDaysList.length > 0) {
        const randomIndex = Math.floor(Math.random() * menusUsedInLastTwoDaysList.length);
        selectedMenu = menusUsedInLastTwoDaysList[randomIndex];
      }
      // اگر هیچ منویی پیدا نشد، از همه منوهای موجود انتخاب کن
      else {
        const randomIndex = Math.floor(Math.random() * availableGroupMenus.length);
        selectedMenu = availableGroupMenus[randomIndex];
      }
    } 
    // اولویت 2: اگر منوهای بدون گروه موجود هستند، از آن‌ها انتخاب کن
    else if (menusWithoutGroups.length > 0) {
      const availableNonGroupMenus = menusWithoutGroups.filter((menu) => {
        const menuId = String(menu.id);
        
        const usedTodayOtherMeals = todayMenus.some((entry) => 
          String(entry.menuId) === menuId && entry.meal !== mealType
        );

        const usedYesterdaySameMeal = yesterdayMenus.some((entry) => 
          String(entry.menuId) === menuId && entry.meal === mealType
        );

        return !usedTodayOtherMeals && !usedYesterdaySameMeal;
      });

      if (availableNonGroupMenus.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableNonGroupMenus.length);
        selectedMenu = availableNonGroupMenus[randomIndex];
      } else {
        const randomIndex = Math.floor(Math.random() * menusWithoutGroups.length);
        selectedMenu = menusWithoutGroups[randomIndex];
      }
    }
    // اولویت 3: اگر هیچ منوی مناسب پیدا نشد، از منوهای بدون گروه انتخاب کن
    // اما فقط اگر واقعاً هیچ منوی گروهی قابل استفاده نیست
    else if (menusWithoutGroups.length > 0) {
      const randomIndex = Math.floor(Math.random() * menusWithoutGroups.length);
      selectedMenu = menusWithoutGroups[randomIndex];
    }
    // اولویت 4: حالت اضطراری - اول از منوهای بدون گروه که تیک "انتخاب غذا" دارند
    // اگر هیچ منوی بدون گروهی نبود، از منوهای به سقف رسیده که قوانین مهم را رعایت می‌کنند
    else if (mealMenus.length > 0) {
      // فیلتر کردن منوهای بدون گروه که selected_food دارند
      const menusWithoutGroupWithSelectedFood = mealMenus.filter((menu) => {
        // فقط منوهای بدون گروه
        if (menu.group_number || menu.group_title) {
          return false;
        }
        
        // باید selected_food داشته باشد
        if (!menu.selected_food || 
            typeof menu.selected_food !== "string" || 
            menu.selected_food.trim().length === 0) {
          return false;
        }
        
        return true;
      });
      
      if (menusWithoutGroupWithSelectedFood.length > 0) {
        const randomIndex = Math.floor(Math.random() * menusWithoutGroupWithSelectedFood.length);
        selectedMenu = menusWithoutGroupWithSelectedFood[randomIndex];
      } else {
        // اگر هیچ منوی بدون گروه با selected_food نبود، از منوهای بدون گروه استفاده کن
        const menusWithoutGroupOnly = mealMenus.filter((menu) => {
          return !menu.group_number && !menu.group_title;
        });
        
        if (menusWithoutGroupOnly.length > 0) {
          const randomIndex = Math.floor(Math.random() * menusWithoutGroupOnly.length);
          selectedMenu = menusWithoutGroupOnly[randomIndex];
        } else {
          // اگر هیچ منوی بدون گروهی وجود نداشت، از منوهای به سقف رسیده که قوانین مهم را رعایت می‌کنند استفاده کن
          const menusAtLimitButValid = menusWithGroups.filter((menu) => {
            const groupKey = `${String(menu.group_number)}_${String(menu.group_title)}`;
            const groupUsage = groupUsageCount[groupKey] || 0;
            const repetitionCount = menu.group_repetition_count || 0;
            
            // باید به سقف رسیده باشد
            if (groupUsage < repetitionCount) {
              return false;
            }
            
            // اما قوانین مهم باید رعایت شوند
            const criticalCheck = checkCriticalRules(menu);
            return criticalCheck.valid;
          });
          
          if (menusAtLimitButValid.length > 0) {
            const randomIndex = Math.floor(Math.random() * menusAtLimitButValid.length);
            selectedMenu = menusAtLimitButValid[randomIndex];
          } else {
            // اگر هیچ منوی به سقف رسیده‌ای که قوانین را رعایت کند نبود، از منوهای دارای گروه که قوانین مهم را رعایت می‌کنند استفاده کن
            // (حتی اگر به سقف نرسیده‌اند)
            const menusWithGroupsButValidRules = menusWithGroups.filter((menu) => {
              const criticalCheck = checkCriticalRules(menu);
              return criticalCheck.valid;
            });
            
            if (menusWithGroupsButValidRules.length > 0) {
              const randomIndex = Math.floor(Math.random() * menusWithGroupsButValidRules.length);
              selectedMenu = menusWithGroupsButValidRules[randomIndex];
            } else {
              // اگر هیچ منویی که قوانین مهم را رعایت کند نبود، از همه منوهای دارای گروه استفاده کن (آخرین حالت)
              // حتی اگر قوانین را نقض می‌کنند - هیچ وقت خطا نمی‌دهیم
              const randomIndex = Math.floor(Math.random() * menusWithGroups.length);
              selectedMenu = menusWithGroups[randomIndex];
            }
          }
        }
      }
    }

    if (!selectedMenu) {
      // اگر هنوز منویی انتخاب نشده، از اولین منوی موجود استفاده کن (حالت اضطراری نهایی)
      // هیچ وقت خطا نمی‌دهیم
      if (mealMenus.length > 0) {
        selectedMenu = mealMenus[0];
      } else {
        // فقط در صورتی که واقعاً هیچ منویی وجود نداشته باشد خطا می‌دهیم
        console.error(`[${mealType}] ❌ هیچ منوی معتبری یافت نشد.`);
        return null;
      }
    }
    
    // بررسی meal منوی انتخاب شده - فقط برای منوهای مشکوک
    const menuMeal = String(selectedMenu.meal || '').trim();
    const targetMealType = String(mealType || '').trim();
    if (menuMeal !== targetMealType) {
      console.warn(`⚠️ [${mealType}] منوی انتخاب شده meal نامطابق دارد:`, {
        id: selectedMenu.id,
        meal: menuMeal,
        mealType: targetMealType,
        text: selectedMenu.text?.substring(0, 100) + '...'
      });
    }
    
    // به‌روزرسانی تاریخچه
    const updatedHistory = updateMenuHistory(currentDate, mealType, selectedMenu.id, selectedMenusHistory);
    
    // اضافه کردن به لیست منوهای انتخاب شده در همان روز
    todaySelectedMenusRef.menus.push({
      date: currentDate,
      meal: mealType,
      menuId: selectedMenu.id
    });
    
    if (onHistoryUpdate && updatedHistory) {
      const filteredDaily = updatedHistory.daily.filter(entry => {
        const entryDate = moment(entry.date, 'YYYY/MM/DD');
        return entryDate.isSameOrAfter(currentWeekStart, 'day') && 
               entryDate.isSameOrBefore(currentWeekEnd, 'day');
      });
      const filteredHistory = {
        ...updatedHistory,
        daily: filteredDaily
      };
      onHistoryUpdate(filteredHistory);
    }
    
    // پردازش آیتم‌ها
    const values = selectedMenu.items.map((item) => {
      const percentage = item.percentage / 100;
      const itemCalories = item.calories;
      const targetCalories = percentage * calories;
      
      let result;
      switch (item.rounding_method) {
        case "round_half":
          result = customRound(targetCalories / itemCalories);
          break;
        case "round_5g":
          result = customRound2(targetCalories / itemCalories);
          break;
        case "round_quarter":
          result = categorizeFraction(targetCalories / itemCalories);
          break;
        case "round_full":
          result = customRoundFull(targetCalories / itemCalories);
          break;
        default:
          result = Math.round(targetCalories / itemCalories);
      }
      
      // اطمینان از اینکه نتیجه حداقل 1 باشد (مگر اینکه targetCalories صفر باشد)
      if (targetCalories > 0 && item.rounding_method !== "round_quarter") {
        result = Math.max(result, 1);
      }

      return result;
    });
    
    // تولید متن آیتم‌ها
    let resultText = selectedMenu.text;
    const dollarSigns = (selectedMenu.text.match(/\$/g) || []).length;
    if (dollarSigns !== values.length) {
      console.warn(`تعداد $ (${dollarSigns}) با تعداد آیتم‌ها (${values.length}) مطابقت ندارد`);
    }
    values.forEach((value) => {
      resultText = resultText.replace(`$`, value);
    });
    
    // انتخاب و پردازش سفره‌چین
    const foldNappes = selectedMenu.fold_nappes || [];
    if (foldNappes.length > 0) {
      let selectedFoldNappe;
      if (selectedMenu.selected_fold_nappe) {
        selectedFoldNappe = foldNappes.find((foldNappe) => foldNappe.name === selectedMenu.selected_fold_nappe);
      }
      if (!selectedFoldNappe) {
        const randomFoldNappeIndex = Math.floor(Math.random() * foldNappes.length);
        selectedFoldNappe = foldNappes[randomFoldNappeIndex];
      }

      // محاسبه مقدار سفره‌چین
      const percentage = selectedFoldNappe.percentage / 100;
      const foldNappeCalories = selectedFoldNappe.calories;
      const targetCalories = percentage * calories;
      
      let foldNappeValue;
      switch (selectedFoldNappe.rounding_method) {
        case "round_half":
          foldNappeValue = customRound(targetCalories / foldNappeCalories);
          break;
        case "round_5g":
          foldNappeValue = customRound2(targetCalories / foldNappeCalories);
          break;
        case "round_quarter":
          foldNappeValue = categorizeFraction(targetCalories / foldNappeCalories);
          break;
        case "round_full":
          foldNappeValue = customRoundFull(targetCalories / foldNappeCalories);
          break;
        default:
          foldNappeValue = Math.round(targetCalories / foldNappeCalories);
      }
      
      if (targetCalories > 0 && selectedFoldNappe.rounding_method !== "round_quarter") {
        foldNappeValue = Math.max(foldNappeValue, 1);
      }
      
      let foldNappeText = selectedFoldNappe.arrangement;
      if (foldNappeText.includes("$")) {
        foldNappeText = foldNappeText.replace("$", foldNappeValue);
      } else {
        foldNappeText = `${foldNappeValue} ${foldNappeText}`;
      }
      
      resultText = resultText + " + " + foldNappeText;
    }
    
    return {
      text: resultText,
      menuId: selectedMenu.id
    };
    
  } catch (error) {
    console.error(`خطا در تولید منوی ${mealType}:`, error);
    return null;
  }
};


